import { Select } from 'antd';
import classNames from 'classnames';
import getUnicodeFlagIcon from 'country-flag-icons/unicode';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const { Option } = Select;

interface OptionType {
  value: string;
  label: string;
  divider?: boolean;
}

interface ArrowProps {
  isOpen: boolean;
}

interface CountrySelectProps {
  value: string;
  onChange: (value?: string) => void;
  options: OptionType[];
  disabled?: boolean;
  readOnly?: boolean;
  width: number | undefined;

  [rest: string]: any; // To allow other props
}

export const CountrySelect: React.FC<CountrySelectProps> = ({
  value,
  onChange,
  options,
  disabled,
  readOnly,
  width = 200,
  ...rest
}) => {
  const onChange_ = useCallback(
    (selectedValue: string) => {
      onChange(selectedValue === 'ZZ' ? undefined : selectedValue);
    },
    [onChange]
  );

  return (
    <Select
      {...rest}
      size={'large'}
      disabled={disabled || readOnly}
      value={value || 'ZZ'}
      onChange={onChange_}
      dropdownStyle={{
        minWidth: '200px',
        width: `${width}px`,
      }}
    >
      {options.map(({ value, label, divider }) => {
        const key = value ?? label; // Ensure key is not undefined
        return divider ? (
          <Option key={key} disabled>
            {label}
          </Option>
        ) : (
          <Option key={key} value={value || 'ZZ'}>
            {label}
          </Option>
        );
      })}
    </Select>
  );
};

interface CountrySelectWithIconProps extends CountrySelectProps {
  iconComponent?: React.ComponentType<{
    ariaHidden?: boolean;
    country?: string;
    label?: string;
    aspectRatio?: number;
    className?: string;
  }>;
  arrowComponent?: React.ElementType;
  unicodeFlags?: boolean;
}

export const CustomAntCountrySelect: React.FC<CountrySelectWithIconProps> = ({
  value,
  options,
  className,
  iconComponent: Icon,
  arrowComponent: Arrow = DefaultArrowComponent,
  unicodeFlags,
  ...rest
}) => {
  const [parentWidth, setParentWidth] = useState<number | undefined>(undefined);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const phoneInputCountryRef = useRef<HTMLDivElement>(null);
  const selectedOption = useMemo(() => {
    return getSelectedOption(options, value);
  }, [options, value]);

  useEffect(() => {
    if (phoneInputCountryRef.current?.parentElement) {
      setParentWidth(phoneInputCountryRef.current.parentElement.offsetWidth); // Set the parent element width
    }
  }, []);
  return (
    <div className="PhoneInputCountry" ref={phoneInputCountryRef}>
      <CountrySelect
        {...rest}
        width={parentWidth}
        value={value}
        options={options}
        className={classNames('PhoneInputCountrySelect', className)}
        onDropdownVisibleChange={() => setIsOpen((prevState) => !prevState)}
      />
      {selectedOption &&
        (unicodeFlags && value ? (
          <div className="PhoneInputCountryIconUnicode">{getUnicodeFlagIcon(value)}</div>
        ) : (
          Icon && (
            <Icon aria-hidden country={value} label={selectedOption.label} aspectRatio={unicodeFlags ? 1 : undefined} />
          )
        ))}
      <Arrow isOpen={isOpen} />
    </div>
  );
};
const DefaultArrowComponent: React.FC<ArrowProps> = ({ isOpen }) => {
  return (
    <div className={classNames('PhoneInputCountrySelectCustomArrow', { open: isOpen })} style={{ marginLeft: 8 }}>
      <svg width="10" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 1L7 7L13 1" stroke="#222530" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
};

function getSelectedOption(options: OptionType[], value?: string) {
  for (const option of options) {
    if (!option.divider) {
      if (isSameOptionValue(option.value, value)) {
        return option;
      }
    }
  }
}

function isSameOptionValue(value1?: string, value2?: string) {
  if (value1 === undefined || value1 === null) {
    return value2 === undefined || value2 === null;
  }
  return value1 === value2;
}
